import type {
  NodeDescriptor,
  StrategyCatalogDto,
  StrategyDocumentDto,
  StrategyResourceDto,
  StrategySummaryDto,
  StrategyDiagnostic,
} from './types';
import { api } from './client';

export interface StrategyValidationResponse {
  isValid: boolean;
  errors: string[];
  diagnostics?: StrategyDiagnostic[];
}

export class StrategiesService {
  async list(signal?: AbortSignal): Promise<StrategySummaryDto[]> {
    return api.get<StrategySummaryDto[]>('/api/strategies', { signal });
  }

  async get(id: string, signal?: AbortSignal): Promise<StrategyDocumentDto> {
    return (await this.getResource(id, signal)).document;
  }

  async getResource(id: string, signal?: AbortSignal): Promise<StrategyResourceDto> {
    return api.get<StrategyResourceDto>(`/api/strategies/${encodeURIComponent(id)}`, { signal });
  }

  async create(doc: StrategyDocumentDto): Promise<StrategyResourceDto> {
    return api.post<StrategyResourceDto>('/api/strategies', { document: doc });
  }

  async update(id: string, doc: StrategyDocumentDto, expectedRevision: number): Promise<StrategyResourceDto> {
    return api.put<StrategyResourceDto>(`/api/strategies/${encodeURIComponent(id)}`, { document: doc, expectedRevision });
  }

  async updateName(id: string, name: string): Promise<StrategySummaryDto> {
    return api.patch<StrategySummaryDto>(`/api/strategies/${encodeURIComponent(id)}/name`, { name });
  }

  async clone(id: string, name?: string): Promise<StrategySummaryDto> {
    return api.post<StrategySummaryDto>(`/api/strategies/${encodeURIComponent(id)}/clone`, { name });
  }

  async delete(id: string): Promise<void> {
    await api.delete<void>(`/api/strategies/${encodeURIComponent(id)}`);
  }

  async validate(doc: StrategyDocumentDto): Promise<StrategyValidationResponse> {
    return api.post<StrategyValidationResponse>('/api/strategies/validate', doc);
  }

  async catalog(signal?: AbortSignal): Promise<StrategyCatalogDto> {
    return api.get<StrategyCatalogDto>('/api/strategy-catalog', { signal });
  }

  async nodeTypes(signal?: AbortSignal): Promise<NodeDescriptor[]> {
    return (await this.catalog(signal)).primitives;
  }
}

export const strategies = new StrategiesService();
